import { useSearchImagesMutation } from "@api/media";
import Icon from "@components/ui/icon";
import { MediaLibFileCheckbox } from "@editor/modules/media-lib/components/files/file";
import { makeImagesPanelImageElement } from "@editor/modules/media-lib/utils";
import { cn } from "@lib/cn";
import { type ReactNode, memo, useMemo } from "react";
import { Blurhash } from "react-blurhash";
import type { ListRowProps } from "react-virtualized";
import {
  IMAGES_PANEL_MASONRY_GAPS,
  STOCK_IMAGES_QUERY_KEYS,
} from "../../constants";
import { useStockImagesCtx } from "../../ctx";
import type { IImagesPanelImage } from "../../types";
import { ImageSkeleton } from "../image-skeleton";

interface IProps {
  rowData: ListRowProps;
  imageData?: IImagesPanelImage | null;
}

export const ImagesPanelStockMasonryRow = ({
  rowData: { style },
  imageData,
}: IProps) => {
  const [, { isLoading }] = useSearchImagesMutation({
    fixedCacheKey: STOCK_IMAGES_QUERY_KEYS,
  });

  const { isMultiSelect, onSelect, selectedItems, view } = useStockImagesCtx();

  const isSelected = useMemo(
    () => !!selectedItems?.find((el) => el.id === imageData?.id),
    [selectedItems, imageData?.id],
  );
  const height = (style.height as number) - IMAGES_PANEL_MASONRY_GAPS;
  if (!imageData) {
    return (
      <div
        style={{
          ...style,
          height,
          opacity: isLoading ? 1 : 0,
        }}
      >
        <ImageSkeleton />
      </div>
    );
  }

  return (
    <Wrapper imageData={imageData}>
      <div
        onClick={() => {
          if (view === "dialog") {
            onSelect?.(imageData);
          }
        }}
        className="tw-group tw-relative"
        style={{
          ...style,
          height,
        }}
      >
        <div className="tw-absolute tw-left-0 tw-top-0 tw-size-full tw-animate-skeleton-loading tw-rounded tw-bg-blue-50" />
        {isMultiSelect && <MediaLibFileCheckbox isSelected={isSelected} />}
        {imageData.imageBlurhash && (
          <div className="tw-absolute tw-left-0 tw-top-0 tw-size-full tw-overflow-hidden tw-rounded">
            <Blurhash
              hash={imageData.imageBlurhash}
              width={style.width}
              height={style.height}
            />
          </div>
        )}

        <img
          key={imageData.id}
          loading="lazy"
          src={imageData.thumbUrl}
          alt={imageData.alt}
          className={cn(
            "tw-relative tw-z-10 tw-size-full tw-cursor-pointer tw-rounded tw-object-cover",
            "tw-border tw-border-solid  tw-border-primary-900/20",
            "tw-outline-none tw-outline-1 tw-outline-offset-1 tw-transition-[outline-color] hover:tw-outline-primary",
          )}
        />

        <div
          className="images-panel-img-user tw-absolute tw-bottom-0 tw-left-0 tw-z-10 tw-w-full tw-rounded-b tw-px-1 tw-pb-2 tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100"
          style={{
            background:
              "linear-gradient(0deg, #0D0D0D 9%, rgba(13, 13, 13, 0.00) 100%)",
          }}
        >
          <a
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="tw-flex tw-items-center tw-text-[10px] tw-font-semibold !tw-text-white tw-underline visited:tw-text-white hover:tw-text-white active:tw-text-white"
            rel="noopener noreferrer"
            href={imageData.userUrl}
            target="_blank"
          >
            <Icon
              icon="ri-external-link-line"
              className="tw-mr-1 tw-text-[12px]/none tw-text-white"
            />
            {imageData.userName}
          </a>
        </div>
      </div>
    </Wrapper>
  );
};

const Wrapper = memo(
  ({
    children,
    imageData,
  }: {
    children: ReactNode;
    imageData: IImagesPanelImage;
  }) => {
    const { elementWrapper } = useStockImagesCtx();
    if (!elementWrapper) return <>{children}</>;
    const ElementWrapper = elementWrapper;

    return (
      <ElementWrapper element={makeImagesPanelImageElement(imageData)}>
        {children}
      </ElementWrapper>
    );
  },
);
